/* eslint-disable sort-keys-fix/sort-keys-fix */
import React, { useContext, useState } from 'react';
import Button from '@mui/material/Button';
import CircularProgress from '@mui/material/CircularProgress';
import Drawer from '@mui/material/Drawer';
import FormGroup from '@mui/material/FormGroup';
import FormControlLabel from '@mui/material/FormControlLabel';
import Stack from '@mui/material/Stack';
import Switch from '@mui/material/Switch';
import Tooltip from '@mui/material/Tooltip';
import Typography from '@mui/material/Typography';
import useMediaQuery from '@mui/material/useMediaQuery';
import { MDialog } from 'common';
import InfoRounded from '@mui/icons-material/InfoRounded';

import * as R from 'ramda';
import { arrayOf, bool, func, shape } from 'prop-types';

import { CreateReconsignmentContext, initialFilters, setup } from '../Create';
import Configuration from './Configuration';
import Filters from './Filters';
import BulkFilters from './BulkFilters';

const Parameters = ({
  isDrawerOpen,
  isLoading,
  bulk,
  results,
  setIsDrawerOpen,
  setSourceLocations,
  setBulkRows,
  resetBulk,
}) => {
  const lgUp = useMediaQuery(theme => theme.breakpoints.up('lg'), { noSsr: true });
  const { filters, info, setFilters, setInfo } = useContext(CreateReconsignmentContext);

  const [openDialog, setOpenDialog] = useState(false);

  const handleClearAll = () => {
    setInfo(setup);
    setFilters(initialFilters);
    setSourceLocations(null);
    if (bulk && setBulkRows) {
      setBulkRows([]);
      resetBulk();
    }
  };

  return (
    <>
      <Drawer
        anchor={lgUp ? 'left' : 'right'}
        onClose={() => setIsDrawerOpen(false)}
        open={isDrawerOpen}
        sx={{
          '& .MuiDrawer-paper': {
            '&::-webkit-scrollbar': {
              display: 'none',
              msOverflowStyle: 'none',
              scrollbarWidth: 'none',
            },
            boxSizing: 'border-box',
            height: 'calc(100% - 100px)',
            mt: '100px',
            pb: 9,
            pt: 2,
            width: { xs: '100%', lg: 395 },
            zIndex: 10,
          },
          backgroundColor: 'surface.white',
          borderRight: '1px solid',
          borderRightColor: 'graphic.light',
          left: 0,
          overflowY: 'auto',
          position: { xs: 'initial', lg: 'fixed' },
          top: 48,
          width: { xs: '100%', lg: 395 },
        }}
        variant={lgUp ? 'permanent' : 'temporary'}
      >
        <Stack spacing={2}>
          <Stack spacing={2} sx={{ pb: 2, px: 2 }}>
            <Stack alignContent="center" direction="row" justifyContent="space-between">
              <Typography color="text.secondary" variant="h7">
                Parameters
              </Typography>
              <Button
                onClick={() => handleClearAll()}
                // eslint-disable-next-line sort-keys-fix/sort-keys-fix
                sx={{ alignSelf: 'flex-end', display: { xs: 'none', lg: 'inline-flex' } }}
                variant="text"
              >
                Clear All
              </Button>
            </Stack>
            {R.not(bulk) && (
              <FormGroup>
                <FormControlLabel
                  control={
                    <Switch
                      checked={R.prop('isRecurring', info)}
                      color="secondary"
                      onChange={e => {
                        const val = R.path(['target', 'checked'], e);
                        if (
                          R.and(
                            val,
                            R.or(
                              R.gt(R.length(R.prop('destinations', info)), 1),
                              R.gt(R.length(R.prop('sourceLocations', info)), 1)
                            )
                          )
                        ) {
                          setOpenDialog(true);
                        } else if (val) {
                          setInfo(
                            R.evolve({
                              fromLocations: R.always([]),
                              isRecurring: R.always(val),
                              destinations: R.always({}),
                              product: R.always(null),
                              toLocations: R.always([]),
                            })
                          );
                          setFilters(R.assoc('origins', {}, filters));
                        } else {
                          setInfo(
                            R.evolve({
                              fromLocations: R.always([]),
                              isRecurring: R.always(val),
                              destinations: R.always([]),
                              product: R.always(null),
                              toLocations: R.always([]),
                            })
                          );
                          setFilters(R.assoc('origins', [], filters));
                        }
                        setFilters(initialFilters);
                        setSourceLocations(null);
                      }}
                    />
                  }
                  label={
                    <Stack alignItems="center" direction="row" justifyContent="space-between">
                      <Typography variant="body2">
                        Do you want to make this a reocurring reconsignment?
                      </Typography>
                      <Tooltip title="Only 1 destination and 1 source location selection is allowed when creating a reoccurring reconsignment">
                        <InfoRounded />
                      </Tooltip>
                    </Stack>
                  }
                  slotProps={{
                    typography: { sx: { color: 'text.secondary' }, variant: 'body2' },
                  }}
                />
              </FormGroup>
            )}
          </Stack>
          <Configuration bulk={bulk} handleClearAll={handleClearAll} />
          {!bulk ? <Filters isLoading={isLoading} /> : <BulkFilters />}
          <Stack
            alignItems="center"
            direction="row"
            justifyContent="space-between"
            spacing={2}
            sx={{
              backgroundColor: 'surface.white',
              bottom: 0,
              boxShadow: 0,
              display: { xs: 'flex', lg: 'none' },
              position: 'fixed',
              px: 3,
              py: 2,
              width: 1,
              zIndex: 1200,
            }}
          >
            <Button onClick={() => handleClearAll()} sx={{ width: 1 }} variant="text">
              Clear All
            </Button>
            <Button
              disabled={R.or(
                isLoading,
                R.or(R.isNil(R.prop('product', info)), R.isEmpty(R.prop('destinations', info)))
              )}
              onClick={() => {
                setIsDrawerOpen(false);
              }}
              sx={{ width: 1 }}
            >
              {isLoading ? (
                <CircularProgress size={20} />
              ) : R.equals(NaN, R.length(results)) ? (
                '0 results'
              ) : (
                `See ${R.length(results)} results`
              )}
            </Button>
          </Stack>
        </Stack>
      </Drawer>
      <MDialog
        header="Alert"
        leftButtonAction={() => {
          setOpenDialog(false);
          setInfo(
            R.evolve({
              isRecurring: R.always(true),
              destinations: R.always([]),
              product: R.always(null),
            }),
            info
          );
        }}
        leftButtonText="Continue"
        onClose={() => setOpenDialog(false)}
        open={openDialog}
        rightButtonAction={() => setOpenDialog(false)}
        rightButtonText="Back"
      >
        <Typography color="textPrimary" component="span" variant="subtitle1">
          Only 1 destination and 1 source location can be selected for a Reoccurring Reconsignment.
          Please select only 1 of each.
        </Typography>
      </MDialog>
    </>
  );
};

Parameters.propTypes = {
  isDrawerOpen: bool.isRequired,
  isLoading: bool.isRequired,
  bulk: bool,
  results: arrayOf(shape({})),
  setIsDrawerOpen: func.isRequired,
  setSourceLocations: func.isRequired,
  setBulkRows: func,
  resetBulk: func,
};

Parameters.defaultProps = {
  bulk: false,
  results: [],
  setBulkRows: null,
  resetBulk: null,
};

export default Parameters;
